import http from "@/services/http";
import { defineStore } from "pinia";
import {ref,computed} from 'vue';
import router from '@/router/index.js';

export const useAuth = defineStore('auth', () => {
    const token = ref(localStorage.getItem('token'));
    const userJson = ref(localStorage.getItem('user'));
    const tenantJson = ref(localStorage.getItem('tenant'));

    const user = computed(() => {
        if (!userJson.value) return null;
        try {
            return typeof userJson.value === 'string' 
                ? JSON.parse(userJson.value) 
                : userJson.value;
        } catch (e) {
            console.error('Erro ao parsear usuário:', e);
            return null;
        }
    });

    const tenant = computed(() => {
        if (!tenantJson.value) return null;
        try {
            return typeof tenantJson.value === 'string' 
                ? JSON.parse(tenantJson.value) 
                : tenantJson.value;
        } catch (e) {
            console.error('Erro ao parsear tenant:', e);
            return null;
        }
    });

    const lastVerification = ref(localStorage.getItem('lastVerification') || 0);

    // Suporte: sessão do administrador da plataforma guardada enquanto ele usa o painel de um cliente
    const ADMIN_SESSION_KEY = 'admin_session';
    const impersonating = ref(!!localStorage.getItem(ADMIN_SESSION_KEY));

    function setToken(tokenValue) {
        localStorage.setItem('token', tokenValue);
        // clear session-toast suppression when a new token is set (successful login)
        if (tokenValue) {
            localStorage.removeItem('session_toast_shown');
        }
        token.value = tokenValue;
    }

    function setUser(userValue) {
        localStorage.setItem('user', userValue);
        userJson.value = userValue;
    }

    function setTenant(tenantValue) {
        localStorage.setItem('tenant', tenantValue);
        tenantJson.value = tenantValue;
    }

    function getBearer() {
        return {headers: { Authorization : "Bearer "+token.value }}
    }

    function setLastVerification(now = Date.now()) {
        localStorage.setItem('lastVerification', now);
        lastVerification.value = now;
    }

    async function verifyToken() {
        const now = Date.now();
        if (!token.value) { return false }
        if ((now - lastVerification.value) < (5 * 60 * 1000)) { return true } //5 minutos
        try {
            const {data} = await http.get('/verifyToken'); // Interceptor cuida do Bearer
            const isValid = !!data?.user;
            if (isValid) { 
                setLastVerification(now);
                if (data.user) setUser(JSON.stringify(data.user));
                if (data.tenant) setTenant(JSON.stringify(data.tenant));
            }
            return isValid;
        } catch (error) {
            console.log(error?.response?.data);
            return false;
        }
    }

    function clearData() {
        // suppress session toast during explicit logout to avoid duplicates
        try { localStorage.setItem('session_toast_shown', '1'); } catch(e){}
        setToken(null);
        setUser(null);
        setTenant(null);
        setLastVerification(0);
        console.log('Dados de autenticação limpos.');
    }

    // Entra no painel do cliente com o token de suporte; recarrega para zerar o estado do app
    function startImpersonation(data) {
        localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify({ token: token.value, user: userJson.value, tenant: tenantJson.value }));
        setToken(data.token);
        setUser(JSON.stringify(data.user));
        setTenant(data.tenant ? JSON.stringify(data.tenant) : null);
        setLastVerification();
        window.location.href = '/';
    }

    // Encerra o token de suporte e volta para a sessão do administrador
    async function stopImpersonation() {
        try { await http.post('/logout'); } catch (e) { /* token de suporte pode já ter expirado */ }
        let saved = null;
        try { saved = JSON.parse(localStorage.getItem(ADMIN_SESSION_KEY)); } catch (e) { saved = null; }
        localStorage.removeItem(ADMIN_SESSION_KEY);
        impersonating.value = false;
        if (!saved?.token) {
            clearData();
            window.location.href = '/login';
            return;
        }
        setToken(saved.token);
        setUser(saved.user);
        setTenant(saved.tenant);
        setLastVerification(0);
        window.location.href = '/admin';
    }

    // Login/cadastro normal descarta qualquer sessão de suporte esquecida
    function forgetAdminSession() {
        localStorage.removeItem(ADMIN_SESSION_KEY);
        impersonating.value = false;
    }

    async function logout() {
        if (impersonating.value) return stopImpersonation();
        try {
            await http.post('/logout'); // Interceptor cuida do Bearer
        } catch (error) {
            console.log('Erro ao fazer logout:');
            console.log(error?.response?.data);
        } finally {
            clearData();
            router.push({name: 'login'});
        }
    }

    return {token, user, tenant, impersonating, setToken, setUser, setTenant, verifyToken, getBearer, logout, clearData, setLastVerification,
        startImpersonation, stopImpersonation, forgetAdminSession}
})
